<!-- Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved. -->

<template>
  <div :class="showContinueAsGuest ? 'container-cag' : 'container'">
    <div class="signup-header">
      <p>{{ $t("signIn.dontHaveAnAccount") }}</p>
      <router-button-comp
        id="sign-up"
        legacySecondary
        :to="signUpUrl">
        {{ $t("signIn.signUp") }}
      </router-button-comp>
    </div>

    <welcome-header class="signin-title" />

    <div class="signin-form">
      <h2 v-if="showContinueAsGuest" class="already-with-us">{{ $t("signIn.alreadyWithUs") }}</h2>
      <form @submit.prevent>
        <input-comp
          :id="signInFormRefs.email"
          :ref="signInFormRefs.email"
          :name="signInFormRefs.email"
          class="email input-group--placeholder"
          :label="$t('signIn.email')"
          autocomplete="username"
          placeholder=" "
          autofocus
          v-model="signInForm.email"
          v-model:errorMessages="signInForm.emailErrorMessages"
          :validationRules="[required, emailValidator]"
        ></input-comp>

        <input-comp
          :id="signInFormRefs.password"
          :ref="signInFormRefs.password"
          :name="signInFormRefs.password"
          type="password"
          class="password input-group--placeholder"
          placeholder=" "
          :label="$t('signIn.password')"
          autocomplete="current-password"
          v-model="signInForm.password"
          v-model:errorMessages="signInForm.passwordErrorMessages"
          :validationRules="[required, min(8)]"
        >
          <template #messages="{messageText}">
            <div class="forgot-password">
              <p>{{ messageText }}</p>
              <button-comp
                id="forgotPasswordLink"
                @click="showForgotPasswordModal"
                anchorStyle
                type="button"
              >
                {{ $t("signIn.forgotPassword") }}
              </button-comp>
            </div>
          </template>
        </input-comp>
        <button-comp
          id="submitButton"
          @click="signIn"
          legacyOrange
          large
          type="submit"
        >
          {{ $t("signIn.signIn") }}
        </button-comp>
      </form>
    </div>

    <template v-if="showContinueAsGuest">
      <div class="signup-divider"></div>
      <div class="signup">
        <h2>{{ $t("signIn.newHere") }}</h2>
        <p class="signup-benefit">{{ $t("signIn.signUpBenefit") }}</p>
        <button-comp
          id="sign-up"
          :to="signUpUrl"
          @click="goToSignUp"
          legacyOrange
          large
          >
            {{ $t("signIn.signUp") }}
          </button-comp>
        <i18n-t
          scope="global"
          keypath="signIn.orContinueAsGuest"
          tag="p"
          class="continue-as-guest"
        >
          <template #continueAsGuest>
            <button-comp
              @click="continueAsGuest"
              anchorStyle
            >
              {{ $t("signIn.continueAsGuest")}}
            </button-comp>
          </template>
        </i18n-t>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { mapGetters, mapState } from 'vuex';
import { getterTypes } from '@/store/geolocation';
import forgotPasswordModal from '@/components/auth/modal-forgot-password.vue';
import confirmProfileInfoModal from '@/components/auth/modal-confirm-profile-info.vue';
import resetPasswordModal from '@/components/auth/modal-reset-password.vue';
import welcomeHeader from '@/components/auth/welcome-header.vue';
import { ProfileInfo } from '@/models/models';
import {
  postSignInAsync,
  postContinueAsGuestAsync,
  userFailures,
} from '@/api/userApi';
import * as result from '@/utils/result';
import { isAbortError } from '@/utils/apiUtils';
import {
  errorToast,
  errorBanner,
  popDialog,
} from '@/utils/notificationUtils';
import analytics from '@/utils/analytics';
import { IState } from '@/store';
import { mutationTypes as infoMutations } from '@/store/info';
import { required, emailValidator, min, IErrorMessage, formValidator } from '@/val/customValidation';
import { useHeaderComposable } from '@/composables/headerComposable';

export default {
  setup() {

    const headerComposable = useHeaderComposable();

    headerComposable.createHeaderLinks('signin');

    return {
      required,
      emailValidator,
      min,
      formValidator,
    };
  },
  name: 'signin-view',
  props: {
    initialResetPasswordModal: {
      type: Boolean,
      default: false,
    },
    confirmProfile: {
      type: Boolean,
      default: false,
    },
    timeout: {
      type: Boolean,
      default: false,
    },
    resetPasswordExpired: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    // eslint-disable-next-line vue/no-unused-components
    forgotPasswordModal,
    // eslint-disable-next-line vue/no-unused-components
    resetPasswordModal,
    // eslint-disable-next-line vue/no-unused-components
    confirmProfileInfoModal,
    welcomeHeader,
  },
  data() {
    return {
      signInForm: {
        email: '',
        emailErrorMessages: [] as IErrorMessage[],
        password: '',
        passwordErrorMessages: [] as IErrorMessage[],
      },
      signInFormRefs: {
        email: 'email',
        password: 'password',
      },
    };
  },
  computed: {
    ...mapState({
      countries: (state: any) => (state as IState).geolocation.countries,
      linkGroups: (state: any) => (state as IState).geolocation.linkGroups,
    }),
    ...mapGetters({
      countryByCountryCode: getterTypes.COUNTRY_BY_CODE,
    }),
    signUpUrl() {
      return {
        name: 'Sign Up',
        path: '/signup',
        query: this.query,
      };
    },
    query() {
      if (this.$route && this.$route.query) {
        return this.$route.query;
      }
      return {};
    },
    showContinueAsGuest() {
      return this.query.cag;
    },
  },
  methods: {
    showConfirmInfoModal() {
      const fakeModel = ProfileInfo.defaultDto(); // TODO use model from signin response
      const model = new ProfileInfo(fakeModel);
      popDialog(
        this.$store,
        this.$options.components?.confirmProfileInfoModal,
        {
          initialModel: model,
        }
      );
    },
    async signIn() {
      const validationResults = this.formValidator(this, this.signInFormRefs);

      if (validationResults.length > 0) {
        return;
      }

      const signInResponse = await postSignInAsync({
        email: this.signInForm.email.trim(),
        password: this.signInForm.password,
        returnUrl: this.query.returnUrl as string,
      });
      if (isAbortError(signInResponse)) {
        return;
      }

      if (result.isError(signInResponse)) {
        let text = 'signIn.signInError';
        if (signInResponse.message === userFailures.InvalidEmail) {
          text = 'signIn.signInErrorEmail';
        } else if (
          signInResponse.message === userFailures.InvalidPassword
        ) {
          text = 'signIn.signInErrorPassword';
        }
        errorToast(this.$store, text);
      } else if (signInResponse.result === 'Success') {
        analytics.track('Sign In Completed', {
          email: signInResponse.email,
          title: signInResponse.title,
          firstName: signInResponse.firstName,
          lastName: signInResponse.lastName,
          country: signInResponse.country,
          region: this.countryByCountryCode(signInResponse.country)
            .region,
          phone: signInResponse.phoneNumber,
          postalCode: signInResponse.postalCode,
          ecomLanguage: signInResponse.locale,
          ecomUserId: signInResponse.userId,
        });
        (window as Window).location = signInResponse.returnUrl;
      } else {
        (window as Window).location = signInResponse.returnUrl;
      }
    },
    showForgotPasswordModal() {
      popDialog(
        this.$store,
        this.$options.components?.forgotPasswordModal,
        {
          initialEmail: this.signInForm.email,
        }
      );
    },
    async continueAsGuest() {
      const response = await postContinueAsGuestAsync({
        returnUrl: this.query.returnUrl as string,
      });
      if (isAbortError(response)) {
        return;
      }

      if (result.isError(response)) {
        errorToast(this.$store, 'signIn.signInError');
      } else {
        (window as Window).location = response;
      }
    },
    goToSignUp() {
      this.$router.push(this.signUpUrl);
    }
  },
  created() {
    if (this.query.email) {
      this.signInForm.email = this.query.email as string;
    }

    if (this.initialResetPasswordModal) {
      popDialog(this.$store, this.$options.components?.resetPasswordModal);
    }

    if (this.confirmProfile) {
      this.showConfirmInfoModal();
    }

    if (this.resetPasswordExpired) {
      errorBanner(
        this,
        'resetPasswordExpiredBanner',
        'auth.expiredResetPasswordToken'
      );
    }
  },
  watch: {
    '$i18n.locale': function() {
      this.$store.commit(infoMutations.REFRESH_BANNERS);
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  max-width: 930px;
  padding-top: 90px;
  padding-left: 40px;
  .signin-title {
    max-width: 550px;
  }
}

.signup-header {
  @include font-body-book-regular;
  display: flex;
  align-items: center;
  position: absolute;
  top: 50px;
  right: 40px;
  margin: 0;
  color: $primary-gray;

  :first-child {
    margin-right: 20px;
  }
}

h2 {
  @include font-header-medium-h2;
  color: $primary-gray;
}

.already-with-us {
  margin-bottom: 30px;
}

.container-cag {
  height: 100%;
  max-width: 1000px;
  position: relative;
  display: -ms-grid;
  display: grid;
  -ms-grid-rows: auto auto 1fr;
  grid-template-rows: auto auto 1fr;
  -ms-grid-columns: minmax(1fr, 540px) 1px auto;
  grid-template-columns: minmax(auto, 540px) 1px auto;

  .signin-title {
    margin-top: 50px;
    -ms-grid-row: 2;
    grid-row: 2;
    -ms-grid-column: 1/-1;
    grid-column: 1/-1;
    padding: 60px 60px 30px 60px;
  }

  .signin-form {
    -ms-grid-row: 3;
    grid-row: 3;
    -ms-grid-column: 1;
    grid-column: 1;
    padding: 30px 60px;
    max-width: 600px;
  }

  .signup {
    -ms-grid-row: 3;
    grid-row: 3;
    -ms-grid-column: 3;
    grid-column: 3;
    padding: 30px 60px;
    max-width: 450px;

    &-divider {
      -ms-grid-row: 3;
      grid-row: 3;
      -ms-grid-column: 2;
      grid-column: 2;
      border: 1px solid $secondary-gray;
    }

    &-benefit {
      @include font-body-book-regular;
      color: $primary-gray;
      margin: 30px 0 40px;
    }
  }

  .continue-as-guest {
    margin-top: 20px;
    @include font-body-book-regular;
    color: $primary-gray;

    button {
      display: inline-block;
    }
  }
}

form {
  max-width: 600px;

  .input-group {
    margin: -10px 0 30px;
  }

  .email {
    margin-bottom: 30px;
  }

  .password {
    margin-bottom: 40px;
  }
}

.forgot-password {
  display: flex;
  justify-content: space-between;
  .button {
    color: $secondary-gray;
  }
}
</style>
